import { useEffect, useState } from "react";
import { Polyline, Marker } from "react-leaflet";
import { api, apiError, fileUrl } from "@/lib/api";
import LeafletMap, { FitBounds } from "@/components/map/LeafletMap";
import { StatusChip } from "@/components/StatusChip";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShieldCheck, Loader2, Check, Ban, Pencil, Route as RouteIcon, MapPin, Info, Store, Eye } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "changes_requested", label: "Changes Requested" },
  { key: "rejected", label: "Rejected" },
];

function Preview({ item }) {
  const path = item.source === "route" ? (item.path || []) : (item.lat != null ? [[item.lat, item.lng]] : []);
  if (!path.length) return <div className="h-40 rounded-lg bg-secondary/40 flex items-center justify-center text-xs text-muted-foreground">No map geometry</div>;
  const center = path[0];
  return (
    <LeafletMap height={160} center={center} zoom={13}>
      {item.source === "route" && path.length >= 2 && <Polyline positions={path} pathOptions={{ color: item.color || "#22D3EE" }} />}
      {item.source !== "route" && item.lat != null && <Marker position={[item.lat, item.lng]} />}
      {path.length >= 2 && <FitBounds bounds={path} />}
    </LeafletMap>
  );
}

export default function ModerationQueue({ onCountChange }) {
  const [status, setStatus] = useState("pending");
  const [data, setData] = useState({ items: [], counts: {}, pending_total: 0 });
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // { item, mode: "reject"|"changes", text }
  const [details, setDetails] = useState(null); // item whose full profile is shown
  const [edits, setEdits] = useState({});

  const load = async (st = status) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/moderation/queue", { params: { status: st } });
      setData(data);
      if (onCountChange) onCountChange(data.counts?.pending || 0);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(status); /* eslint-disable-next-line */ }, [status]);

  const base = (item) => {
    if (item.source === "route") return `/admin/routes/${item.id}`;
    if (item.source === "advertiser_profile") return `/admin/advertisers/${item.id}/profile`;
    if (item.source === "shop_profile") return `/admin/shops/${item.id}/profile`;
    return `/admin/content/${item.id}`;
  };
  const approve = async (item) => { try { await api.post(`${base(item)}/approve`, {}); toast.success("Approved — now live for riders"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const archive = async (item) => { try { await api.post(`${base(item)}/archive`, {}); toast.success("Archived"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const openDetails = (it) => { const e = {}; (it.details || []).forEach((r) => { e[r.key] = r.value; }); setEdits(e); setDetails(it); };
  const saveAndApprove = async () => {
    const it = details;
    const changed = {};
    (it.details || []).forEach((r) => { if (edits[r.key] !== r.value) changed[r.key] = edits[r.key]; });
    try {
      if (Object.keys(changed).length) await api.put(base(it), changed);
      await api.post(`${base(it)}/approve`, {});
      toast.success(Object.keys(changed).length ? "Saved & approved — now live for riders" : "Approved — now live for riders");
      setDetails(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const submitDialog = async () => {
    const { item, mode, text } = dialog;
    try {
      if (mode === "reject") await api.post(`${base(item)}/reject`, { reason: text });
      else await api.post(`${base(item)}/request-changes`, { note: text });
      toast.success(mode === "reject" ? "Rejected — partner notified" : "Changes requested — partner notified");
      setDialog(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const items = data.items || [];

  return (
    <div className="space-y-4" data-testid="moderation-queue">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-primary" />
        <div><h2 className="font-heading text-lg font-bold">Review Queue</h2><p className="text-sm text-muted-foreground">Partner-submitted routes and content awaiting moderation.</p></div>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setStatus(t.key)} data-testid={`queue-tab-${t.key}`}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${status === t.key ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-secondary"}`}>
            {t.label}{(data.counts?.[t.key] || 0) > 0 && <span className="ml-1.5 text-xs font-bold">{data.counts[t.key]}</span>}
          </button>
        ))}
      </div>

      {loading ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        : items.length === 0 ? <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center text-muted-foreground" data-testid="queue-empty">Nothing in this queue right now.</div>
        : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((it) => (
            <div key={`${it.source}-${it.id}`} className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`queue-item-${it.id}`}>
              <Preview item={it} />
              <div className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-heading font-bold flex items-center gap-1.5">
                      {it.source === "route" ? <RouteIcon className="w-4 h-4 text-cyan-400" /> : <MapPin className="w-4 h-4 text-emerald-400" />}{it.name}
                    </div>
                    <div className="text-xs text-muted-foreground capitalize">{it.kind}{it.category ? ` · ${it.category}` : ""} · {it.scope}</div>
                  </div>
                  <StatusChip status={it.approval_status} testId={`queue-status-${it.id}`} />
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Store className="w-3.5 h-3.5" />{it.shop_name || "—"}{it.zone_name ? ` · ${it.zone_name}` : ""}</div>
                {it.photos?.length > 0 && (
                  <div className="flex gap-1.5">{it.photos.slice(0, 4).map((p, i) => <img key={i} src={fileUrl(p)} alt="" className="w-12 h-12 rounded-md object-cover" />)}</div>
                )}
                {it.change_request_note && <div className="text-xs rounded-md bg-orange-500/10 border border-orange-500/20 px-2 py-1.5 text-orange-300 flex gap-1.5"><Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />{it.change_request_note}</div>}
                {it.rejection_reason && <div className="text-xs rounded-md bg-red-500/10 border border-red-500/20 px-2 py-1.5 text-red-300 flex gap-1.5"><Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />{it.rejection_reason}</div>}
                <div className="flex flex-wrap gap-2 pt-1">
                  {it.details?.length > 0 && <Button size="sm" variant="outline" onClick={() => openDetails(it)} data-testid={`queue-view-${it.id}`}><Eye className="w-4 h-4 mr-1" />View full profile</Button>}
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500" onClick={() => approve(it)} data-testid={`queue-approve-${it.id}`}><Check className="w-4 h-4 mr-1" />Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => setDialog({ item: it, mode: "changes", text: "" })} data-testid={`queue-request-changes-${it.id}`}><Pencil className="w-4 h-4 mr-1" />Request changes</Button>
                  <Button size="sm" variant="outline" className="text-red-400 border-red-500/30 hover:bg-red-500/10" onClick={() => setDialog({ item: it, mode: "reject", text: "" })} data-testid={`queue-reject-${it.id}`}><Ban className="w-4 h-4 mr-1" />Reject</Button>
                  <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => archive(it)} data-testid={`queue-archive-${it.id}`}>Archive</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="queue-dialog">
          <DialogHeader><DialogTitle>{dialog?.mode === "reject" ? "Reject" : "Request changes"} — {dialog?.item?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{dialog?.mode === "reject" ? "Tell the partner why this was rejected. They'll be emailed and can edit & resubmit." : "Describe what the partner should change. They'll be emailed and can resubmit."}</p>
            <Textarea rows={4} value={dialog?.text || ""} onChange={(e) => setDialog((d) => ({ ...d, text: e.target.value }))} placeholder={dialog?.mode === "reject" ? "Reason for rejection…" : "Requested changes…"} data-testid="queue-dialog-text" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={submitDialog} data-testid="queue-dialog-submit">{dialog?.mode === "reject" ? "Reject & notify" : "Send request"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!details} onOpenChange={(o) => !o && setDetails(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="queue-details-dialog">
          <DialogHeader><DialogTitle>{details?.name} — full submission</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Edit any field to make minor corrections before approving.</p>
          {details?.photos?.length > 0 && (
            <div className="flex gap-2 flex-wrap">{details.photos.slice(0, 8).map((p, i) => <img key={i} src={fileUrl(p)} alt="" className="w-20 h-20 rounded-md object-cover" />)}</div>
          )}
          <div className="divide-y divide-border">
            {(details?.details || []).map((row) => (
              <div key={row.key} className="py-2.5" data-testid={`queue-detail-row-${row.key}`}>
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{row.label}</div>
                {row.type === "bool" ? (
                  <Switch checked={!!edits[row.key]} onCheckedChange={(v) => setEdits((e) => ({ ...e, [row.key]: v }))} data-testid={`queue-edit-${row.key}`} />
                ) : row.type === "long" ? (
                  <Textarea rows={3} value={edits[row.key] ?? ""} onChange={(e) => setEdits((s) => ({ ...s, [row.key]: e.target.value }))} data-testid={`queue-edit-${row.key}`} />
                ) : (
                  <Input value={edits[row.key] ?? ""} onChange={(e) => setEdits((s) => ({ ...s, [row.key]: e.target.value }))} data-testid={`queue-edit-${row.key}`} />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDetails(null)}>Close</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-500" onClick={saveAndApprove} data-testid="queue-details-approve"><Check className="w-4 h-4 mr-1" />Save &amp; Approve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
